import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Field, Input, Select, todayIso } from '../kit';

// Shared pieces for Reports and oversight (CoordinationDashboard). Data comes from /api/sc/insights
// (backend/routes/scReports.js); the permission lists below mirror that router so a tab is only
// offered when its data will load.

export const API = '/sc/insights';
export const PAGE_PERMS = ['view_all_hours', 'claiming', 'admin'];
export const HOURS_PERMS = ['view_all_hours', 'generate_pay_files', 'admin'];
export const CLAIMS_PERMS = ['claiming', 'reconciliation', 'view_all_hours', 'admin'];
export const hasAny = (perms, list) => list.some((p) => perms.includes(p));

export const BASE = '/support-coordination';
export const fileLink = (coordinationId, query) => (coordinationId ? `${BASE}/${coordinationId}${query ? `?${query}` : ''}` : null);
export const viewLink = (query) => `${BASE}?${query}`;

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const hoursText = (minutes) => `${(Number(minutes || 0) / 60).toFixed(2)} h`;

// Dates are plain YYYY-MM-DD strings, compared and shifted as UTC days (no time-zone drift).
const parts = (iso) => String(iso || '').slice(0, 10).split('-').map(Number);
const utc = (iso) => { const [y, m, d] = parts(iso); return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null; };
const iso = (dt) => dt.toISOString().slice(0, 10);
export const addDays = (s, n) => { const dt = utc(s); dt.setUTCDate(dt.getUTCDate() + n); return iso(dt); };
export const shortDate = (s) => { const dt = utc(s); return dt ? dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : ''; };
// "May 2026" (a two-digit year would read as a day: "May 26").
export const monthLabel = (ym) => { const dt = utc(`${String(ym).slice(0, 7)}-01`); return dt ? dt.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : ''; };
export const monthLong = (ym) => { const dt = utc(`${String(ym).slice(0, 7)}-01`); return dt ? dt.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : ''; };
// "12 Oct – 25 Oct 2026", or "28 Dec 2025 – 10 Jan 2026" across a year end.
export function rangeLabel(from, to) {
  const a = utc(from); const b = utc(to);
  if (!a || !b) return '';
  const full = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
  const left = a.getUTCFullYear() === b.getUTCFullYear() ? a.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : a.toLocaleDateString('en-AU', full);
  return `${left} – ${b.toLocaleDateString('en-AU', full)}`;
}

// Calendar presets for the period picker. Australia's financial year starts on 1 July.
export function calendarPresets(today = todayIso()) {
  const [y, m] = parts(today);
  const pad = (n) => String(n).padStart(2, '0');
  const first = (yy, mm) => `${yy}-${pad(mm)}-01`;
  const prevY = m === 1 ? y - 1 : y; const prevM = m === 1 ? 12 : m - 1;
  const back2 = m <= 2 ? { y: y - 1, m: m + 10 } : { y, m: m - 2 };
  const fy = m >= 7 ? y : y - 1;
  return [
    { key: 'this-month', label: 'This month', from: first(y, m), to: today },
    { key: 'last-month', label: 'Last month', from: first(prevY, prevM), to: addDays(first(y, m), -1) },
    { key: 'last-3-months', label: 'Last 3 months', from: first(back2.y, back2.m), to: today },
    { key: 'this-fy', label: 'This financial year', from: `${fy}-07-01`, to: today },
    { key: 'last-fy', label: 'Last financial year', from: `${fy - 1}-07-01`, to: `${fy}-06-30` },
  ];
}

// Period picker: a labelled select of presets, and two labelled dates when "Custom dates" is chosen.
// value = { key, from, to }; presets = [{ key, label, from, to }].
export function PeriodPicker({ value, onChange, presets, label = 'Period', className }) {
  const [draft, setDraft] = React.useState({ from: value.from || '', to: value.to || '' });
  React.useEffect(() => { setDraft({ from: value.from || '', to: value.to || '' }); }, [value.from, value.to]);
  const custom = value.key === 'custom';
  const options = [...presets.map((p) => ({ value: p.key, label: p.label, hint: rangeLabel(p.from, p.to) })), { value: 'custom', label: 'Custom dates' }];
  const pick = (key) => {
    if (key === 'custom') { onChange({ key: 'custom', from: value.from || draft.from, to: value.to || draft.to }); return; }
    const p = presets.find((x) => x.key === key);
    if (p) onChange({ key: p.key, from: p.from, to: p.to });
  };
  const bad = custom && draft.from && draft.to && draft.from > draft.to;
  const setDate = (k, v) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    if (next.from && next.to && next.from <= next.to) onChange({ key: 'custom', from: next.from, to: next.to });
  };
  return (
    <div className={clsx('flex flex-wrap items-start gap-3', className)}>
      <Field label={label} className="w-full sm:w-64"><Select value={value.key} onChange={pick} options={options} /></Field>
      {custom && (
        <>
          <Field label="From" className="w-[calc(50%-0.375rem)] sm:w-40" error={bad ? 'Pick a start date on or before the end date.' : undefined}>
            <Input type="date" value={draft.from} max={draft.to || undefined} onChange={(e) => setDate('from', e.target.value)} />
          </Field>
          <Field label="To" className="w-[calc(50%-0.375rem)] sm:w-40">
            <Input type="date" value={draft.to} min={draft.from || undefined} onChange={(e) => setDate('to', e.target.value)} />
          </Field>
        </>
      )}
    </div>
  );
}

export function CoordinatorPicker({ value, onChange, coordinators, className }) {
  return (
    <Field label="Coordinator" className={clsx('w-full sm:w-64', className)}>
      <Select value={value || ''} onChange={onChange} allowEmpty emptyLabel="All coordinators"
        options={(coordinators || []).map((c) => ({ value: c.id, label: c.name }))} />
    </Field>
  );
}

// A link that looks like the kit's buttons (navigation stays a link, not a <button>).
export function LinkBtn({ to, children, variant = 'ghost', size = 'sm', icon: Icon, className, ...rest }) {
  return (
    <Link to={to} {...rest}
      className={clsx('inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl transition-colors duration-150 whitespace-nowrap',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1',
        size === 'sm' ? 'text-[12.5px] h-10 sm:h-8 px-3' : 'text-[13px] h-11 sm:h-9 px-3.5',
        variant === 'secondary' ? 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300'
          : 'text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900', className)}>
      {Icon && <Icon size={size === 'sm' ? 13 : 15} aria-hidden />}
      {children}
    </Link>
  );
}

// One line of figures (not a wall of tiles): label, then the number in bold.
export function FigureLine({ items, className }) {
  const list = items.filter(Boolean);
  return (
    <dl className={clsx('flex flex-wrap items-baseline gap-x-5 gap-y-1.5 text-[13px]', className)}>
      {list.map((x) => (
        <div key={x.label} className="flex items-baseline gap-1.5">
          <dt className="text-slate-500">{x.label}</dt>
          <dd className={clsx('font-semibold tabular-nums', x.tone === 'amber' ? 'text-amber-800' : x.tone === 'red' ? 'text-rose-700' : 'text-slate-900')}>{x.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// "Show all 12" / "Show fewer" under a cut-down list.
export function MoreToggle({ shown, total, open, onToggle, noun = '' }) {
  if (total <= shown && !open) return null;
  return (
    <button type="button" onClick={onToggle} aria-expanded={open}
      className="min-h-[44px] sm:min-h-0 sm:py-1.5 text-[13px] font-semibold text-indigo-700 hover:text-indigo-900 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      {open ? 'Show fewer' : `Show all ${total}${noun ? ` ${noun}` : ''}`}
    </button>
  );
}
